/**
 * Écran d'une discussion : fil FUSIONNÉ (messages asynchrones + ceux des
 * salles visio rattachées, `useDiscussionMessages` → `mergeDiscussionTimeline`)
 * + composeur (texte + fichier). Implémentation PROPRE à `apps/app`, aucune
 * dépendance vers `packages/visio` — mirroir visuel de `RendezVousDialogue`/
 * `ChatPanel`, mais un seul système d'écriture par origine de message (cf.
 * `useSetMessageHidden`).
 */

import { useEffect, useRef, useState } from 'react';
import { Flag, Paperclip, RotateCcw, ShieldAlert, Video, X } from 'lucide-react';
import { Button, Input, cn } from '@jeraisonne/ui';
import { DOCUMENT_ACCEPT } from '@jeraisonne/documents';
import { AvatarCircle } from '@/components/avatar-circle';
import { openDocumentById } from '@/lib/documents/open-by-id';
import { useJoinVisioRoom } from '@/lib/visio/handoff';
import { useAuth } from '@/lib/auth';
import {
  DiscussionError,
  discussionOtherParty,
  type DiscussionRow,
  type DiscussionTimelineMessage,
} from '@/lib/discussions/types';
import { DISCUSSION_PLACEHOLDER_TITLE } from '@/lib/discussions/discussion-subject';
import {
  useDiscussionMessages,
  useDiscussionRealtime,
  useGenerateDiscussionTitle,
  useMarkDiscussionRead,
  useSendDiscussionFile,
  useSendDiscussionMessage,
  useSetMessageHidden,
  useStartCallFromDiscussion,
} from '@/lib/discussions/queries';
import { useDiscussionReadHeartbeat } from '@/lib/discussions/read-heartbeat';
import { useIsBlockedByMe, useUnblockUser } from '@/lib/safety/user-safety';
import { ReportSheet } from '@/features/safety/report-sheet';
import { UserSafetySheet } from '@/features/safety/user-safety-sheet';

const TIME = new Intl.DateTimeFormat('fr-FR', {
  timeZone: 'Europe/Paris',
  hour: '2-digit',
  minute: '2-digit',
});

export function DiscussionThread({
  discussion,
  myUserId,
  myRole,
}: {
  discussion: DiscussionRow;
  myUserId: string;
  myRole: 'tutor' | 'student';
}) {
  const messages = useDiscussionMessages(discussion.id);
  // Temps réel : les messages de l'autre (et des visios rattachées) arrivent sans recharger.
  const linkedRoomIds = Array.from(
    new Set(
      (messages.data ?? []).flatMap((m) => (m.origin === 'room' && m.roomId ? [m.roomId] : [])),
    ),
  );
  useDiscussionRealtime(discussion.id, linkedRoomIds);
  const sendMessage = useSendDiscussionMessage(discussion.id);
  const sendFile = useSendDiscussionFile(discussion.id);
  const setHidden = useSetMessageHidden(discussion.id);
  const markRead = useMarkDiscussionRead(discussion.id);
  // Fil ouvert = « en train de lire » : le serveur ne notifie pas (anti-spam).
  useDiscussionReadHeartbeat(discussion.id);
  const startCall = useStartCallFromDiscussion();
  const joinRoom = useJoinVisioRoom();
  const generateTitle = useGenerateDiscussionTitle(discussion.id);

  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Signaler / bloquer (0604, App Review 1.2).
  const [safetyOpen, setSafetyOpen] = useState(false);
  const [reporting, setReporting] = useState<DiscussionTimelineMessage | null>(null);
  const otherId = myRole === 'tutor' ? discussion.studentId : discussion.tutorId;
  const iBlocked = useIsBlockedByMe(otherId);
  const unblock = useUnblockUser();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const markedRef = useRef<string | null>(null);

  // Marque le fil lu une fois par discussion ouverte (pas à chaque re-render).
  useEffect(() => {
    if (markedRef.current === discussion.id) return;
    markedRef.current = discussion.id;
    markRead.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discussion.id]);

  useEffect(() => {
    const list = listRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [messages.data]);

  const { profile } = useAuth();
  const { name: otherName, avatarUrl: otherAvatar } = discussionOtherParty(discussion, myRole);
  const myAvatar = profile?.avatar_url ?? null;

  // Titrage automatique (IA) — un seul déclenchement en pratique : la
  // condition devient fausse dès que le titre change (invalidation React
  // Query après le succès de `generateTitle`). Best-effort, jamais d'erreur
  // affichée : au pire le placeholder reste, état parfaitement acceptable.
  function maybeGenerateTitle() {
    if (discussion.subjectScope === 'free' && discussion.title === DISCUSSION_PLACEHOLDER_TITLE) {
      generateTitle.mutate();
    }
  }

  async function handleSend() {
    const body = draft.trim();
    if (!body || sendMessage.isPending) return;
    setError(null);
    setDraft('');
    try {
      await sendMessage.mutateAsync(body);
      maybeGenerateTitle();
    } catch (err) {
      setError(err instanceof DiscussionError ? err.message : "Le message n'a pas pu être envoyé.");
    }
  }

  async function handleFilePicked(file: File | undefined) {
    if (!file) return;
    setError(null);
    try {
      await sendFile.mutateAsync(file);
      maybeGenerateTitle();
    } catch (err) {
      setError(err instanceof DiscussionError ? err.message : "Le fichier n'a pas pu être envoyé.");
    }
  }

  async function handleStartCall() {
    setError(null);
    try {
      const { reservationId } = await startCall.mutateAsync(discussion);
      joinRoom.mutate(reservationId);
    } catch (err) {
      setError(err instanceof DiscussionError ? err.message : "Impossible de démarrer l'appel.");
    }
  }

  // Le tuteur voit les messages masqués (estompés, pour les rétablir) ; l'élève jamais.
  const visible = (messages.data ?? []).filter((m) => !m.hiddenAt || myRole === 'tutor');

  return (
    // Hauteur donnée par le parent (flex-1 dans une colonne h-dvh) : jamais un
    // `100dvh - X` codé en dur, qui ignorait les zones système iPhone.
    <div className="mx-auto flex min-h-0 w-full max-w-2xl flex-1 flex-col px-4 pt-3 sm:pt-5">
      <header className="mb-3 flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-3">
          {/* Avatar de l'interlocuteur, bien visible en tête du fil. */}
          <AvatarCircle name={otherName} src={otherAvatar} size="hero" />
          <div className="min-w-0">
            <h1 className="font-display text-ink-900 truncate text-lg font-extrabold tracking-tight">
              {discussion.title}
            </h1>
            <p className="text-ink-500 truncate text-sm">Avec {otherName}</p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {myRole === 'tutor' && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void handleStartCall()}
              loading={startCall.isPending || joinRoom.isPending}
              className="min-h-[2.75rem] shrink-0 active:scale-[0.98]"
            >
              <Video className="mr-1.5 size-4" aria-hidden />
              Appeler
            </Button>
          )}
          <button
            type="button"
            onClick={() => setSafetyOpen(true)}
            aria-label={`Signaler ou bloquer ${otherName}`}
            title={`Signaler ou bloquer ${otherName}`}
            className="text-ink-400 hover:text-ink-700 flex size-11 shrink-0 items-center justify-center rounded-full transition-colors active:scale-90"
          >
            <ShieldAlert className="size-5" aria-hidden />
          </button>
        </div>
      </header>

      <div ref={listRef} className="flex-1 space-y-2 overflow-y-auto pb-3">
        {messages.isLoading && <p className="text-ink-400 text-sm">Chargement…</p>}
        {!messages.isLoading && visible.length === 0 && (
          <p className="text-ink-400 py-10 text-center text-sm">Aucun message pour l’instant.</p>
        )}
        {visible.map((m) => {
          const mine = m.senderId === myUserId;
          const hidden = !!m.hiddenAt;
          // Message optimiste (pas encore confirmé par le serveur) : pas d'action.
          const pending = m.rawId.startsWith('pending-');
          const canHide = !pending && (mine || myRole === 'tutor');
          const canReport = !pending && !mine;
          return (
            <div key={m.id} className={cn('flex items-end gap-2', mine && 'flex-row-reverse')}>
              <AvatarCircle
                name={mine ? (profile?.display_name ?? 'Toi') : otherName}
                src={mine ? myAvatar : otherAvatar}
                size="sm"
              />
              <div
                className={cn(
                  'flex max-w-[75%] flex-col gap-1 rounded-2xl px-3 py-2 text-sm',
                  mine ? 'bg-primary-500 text-white' : 'bg-ink-50 text-ink-900',
                )}
              >
                {m.source === 'file' ? (
                  m.documentId ? (
                    <button
                      type="button"
                      onClick={() => {
                        setError(null);
                        openDocumentById(m.documentId as string).catch(() =>
                          setError("Impossible d'ouvrir ce fichier."),
                        );
                      }}
                      className={cn(
                        'inline-flex min-h-[2.75rem] items-center gap-1.5 text-left underline underline-offset-2',
                        hidden && 'line-through opacity-60',
                      )}
                    >
                      <Paperclip className="size-3.5 shrink-0" aria-hidden />
                      <span className="break-words">{m.body}</span>
                    </button>
                  ) : (
                    <span className="italic opacity-70">Fichier supprimé</span>
                  )
                ) : (
                  <span className={cn('break-words', hidden && 'line-through opacity-60')}>
                    {m.body}
                  </span>
                )}
                <span className={cn('text-[11px]', mine ? 'text-white/70' : 'text-ink-400')}>
                  {pending ? 'Envoi…' : TIME.format(new Date(m.createdAt))}
                  {m.origin === 'room' && ' · en appel'}
                </span>
              </div>
              {canHide && (
                <button
                  type="button"
                  onClick={() =>
                    setHidden.mutate({ origin: m.origin, messageId: m.rawId, hidden: !hidden })
                  }
                  aria-label={hidden ? 'Rétablir ce message' : 'Masquer ce message'}
                  title={hidden ? 'Rétablir ce message' : 'Masquer ce message'}
                  className="text-ink-300 hover:text-ink-600 flex size-11 shrink-0 items-center justify-center self-center transition-colors active:scale-90"
                >
                  {hidden ? <RotateCcw className="size-3.5" /> : <X className="size-3.5" />}
                </button>
              )}
              {canReport && (
                <button
                  type="button"
                  onClick={() => setReporting(m)}
                  aria-label="Signaler ce message"
                  title="Signaler ce message"
                  className="text-ink-300 hover:text-ink-600 flex size-11 shrink-0 items-center justify-center self-center transition-colors active:scale-90"
                >
                  <Flag className="size-3.5" aria-hidden />
                </button>
              )}
            </div>
          );
        })}
      </div>

      {error && (
        <p role="alert" className="text-accent-700 mb-2 text-xs font-medium">
          {error}
        </p>
      )}

      {iBlocked ? (
        <div className="border-ink-100 flex shrink-0 flex-col gap-2 border-t pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-ink-600 text-sm">
            Tu as bloqué {otherName} : vous ne pouvez plus vous écrire.
          </p>
          <Button
            type="button"
            variant="outline"
            onClick={() => unblock.mutate(otherId)}
            disabled={unblock.isPending}
            className="min-h-[2.75rem] w-full sm:w-auto"
          >
            Débloquer
          </Button>
        </div>
      ) : (
        <form
          className="border-ink-100 flex shrink-0 gap-2 border-t pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3"
          onSubmit={(e) => {
            e.preventDefault();
            void handleSend();
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={DOCUMENT_ACCEPT}
            className="hidden"
            aria-label="Choisir un fichier à joindre"
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              void handleFilePicked(file);
            }}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={sendFile.isPending}
            aria-label="Joindre un fichier"
            title="Joindre un fichier"
            className="text-ink-500 hover:text-ink-800 flex min-h-[2.75rem] min-w-[2.75rem] shrink-0 items-center justify-center rounded-xl transition-colors active:scale-95 disabled:opacity-50"
          >
            <Paperclip className="size-5" />
          </button>
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Écrire un message…"
            className="min-h-[2.75rem] flex-1"
          />
          <Button
            type="submit"
            disabled={!draft.trim() || sendMessage.isPending}
            className="min-h-[2.75rem]"
          >
            Envoyer
          </Button>
        </form>
      )}

      {safetyOpen && (
        <UserSafetySheet userId={otherId} name={otherName} onClose={() => setSafetyOpen(false)} />
      )}
      {reporting && (
        <ReportSheet
          target={{
            kind: reporting.origin === 'room' ? 'room_message' : 'discussion_message',
            messageId: reporting.rawId,
            userId: reporting.senderId,
          }}
          targetName={otherName}
          excerpt={reporting.source === 'file' ? `Fichier : ${reporting.body}` : reporting.body}
          alreadyBlocked={!!iBlocked}
          onClose={() => setReporting(null)}
        />
      )}
    </div>
  );
}
